import React, { useCallback, useEffect, useMemo, useRef } from "react";

import Konva from "konva";
import { Rect, Group, Line } from "react-konva";

import {
  PADDING_Y,
  POINT_MARKER_SIZE,
  SEGMENT_COLOR_BASE,
  SEGMENT_COLOR_LIGHT,
  POINT_BOX_COLOR_BASE,
  POINT_BOX_COLOR_LIGHT,
  TRACK_COLOR_BASE,
  TRACK_COLOR_LIGHT,
  DIMMED_OPACITY,
  GLOW_COLOR,
  GLOW_BLUR,
  GLOW_OPACITY,
  ANIMATION_DURATION,
} from "../../constants";
import { useTimelineStore } from "../../store/timeline-context";
import { TSegment } from "../../types";
import type { TRenderableSegment } from "../../utils/tree";
import type { TimelineStatusRun } from "../../tracking/player/timeline-status";
import { focusTargetId } from "../../utils/focus";
import { useCoverageReveal, type CoverageArrival } from "./useCoverageReveal";
import type { SegmentGrowth } from "./useSegmentGrowth";
import type { SegmentRetraction } from "./useSegmentRetractions";

const SEGMENT_COLORS: Record<TSegment["type"], { base: string; light: string }> = {
  clip: { base: SEGMENT_COLOR_BASE, light: SEGMENT_COLOR_LIGHT },
  point_box: { base: POINT_BOX_COLOR_BASE, light: POINT_BOX_COLOR_LIGHT },
  // Timeline diamond markers share a palette; the overlay carries the visual
  // distinction between box / point / polygon.
  point: { base: POINT_BOX_COLOR_BASE, light: POINT_BOX_COLOR_LIGHT },
  polygon: { base: POINT_BOX_COLOR_BASE, light: POINT_BOX_COLOR_LIGHT },
};

const TRACK_COLORS = { base: TRACK_COLOR_BASE, light: TRACK_COLOR_LIGHT };

/**
 * `groupId` presence uniquely marks a cluster bar (set by `trackToBars`), so
 * it doubles as the "render as a track bar" signal — the one place the
 * timeline differentiates a track from a point_box.
 */
function colorsFor(segment: TSegment): { base: string; light: string } {
  if ("groupId" in segment && typeof (segment as { groupId?: unknown }).groupId === "string") {
    return TRACK_COLORS;
  }
  return SEGMENT_COLORS[segment.type];
}

type SegmentProps = React.ComponentProps<typeof Rect> & {
  segment: TRenderableSegment;
  pixelsPerSecond: number;
  rowY: number;
  rowHeight: number;
  /** Summary bars belong to their collapsed collection, not the child drawn underneath. */
  collectionId?: string;
  onSegmentClick?: (time: number) => void;
  isHighlighted: boolean | null;
  coverageAdditions?: readonly CoverageArrival[];
  statusRuns?: readonly TimelineStatusRun[];
  growth?: SegmentGrowth;
  retraction?: SegmentRetraction;
};

type PointMarkerProps = SegmentProps;
type DurationRectProps = SegmentProps;

// ============================================================================
// Visual Manager Hook (Animation + Caching)
// ============================================================================

const useSegmentVisuals = (
  groupRef: React.RefObject<Konva.Group | null>,
  shapeRef: React.RefObject<Konva.Shape | null>,
  isHighlighted: boolean | null | undefined,
  glowColor: string = GLOW_COLOR,
  updateDeps: unknown[],
) => {
  const tweensRef = useRef<Konva.Tween[]>([]);
  const previousHighlightRef = useRef<boolean | null | undefined>(isHighlighted);
  // Captured at mount so hover-end can restore the bar to its original
  // z-index. We deliberately *don't* update this on hover-start — if the user
  // hovers A then moves to B without unhovering, A's position has shifted,
  // but its true original index is still its mount-time value, which is what
  // we want to restore to on unhover.
  const originalZIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (groupRef.current && originalZIndexRef.current === null) {
      originalZIndexRef.current = groupRef.current.zIndex();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const groupNode = groupRef.current;
    const shapeNode = shapeRef.current;

    if (tweensRef.current) {
      tweensRef.current.forEach((t) => t.destroy());
      tweensRef.current = [];
    }

    if (!groupNode || !shapeNode) return;

    const highlightChanged = previousHighlightRef.current !== isHighlighted;
    previousHighlightRef.current = isHighlighted;

    // Redraw whenever any item in `updateDeps` changes (width on zoom, label).
    groupNode.getLayer()?.batchDraw();

    // Skip the tween cycle entirely when only geometry/label changed (i.e. no
    // highlight transition needed). At ~50 segments, allocating + destroying
    // 2 Tween instances per segment per zoom tick is what was pushing the rAF
    // handler to 80ms during continuous wheel-zoom. We still want to run a
    // tween when the highlight state actually changes (hover, dim).
    if (!highlightChanged) return;

    // --- Determine Target Values ---
    let targetOpacity = 1;
    let targetShadowBlur = 0;
    let targetShadowOpacity = 0;

    if (isHighlighted === true) {
      targetOpacity = 1;
      targetShadowBlur = GLOW_BLUR;
      targetShadowOpacity = GLOW_OPACITY;
    } else if (isHighlighted === false) {
      targetOpacity = DIMMED_OPACITY;
      targetShadowBlur = 0;
      targetShadowOpacity = 0;
    }

    // --- Create Tweens ---
    const groupTween = new Konva.Tween({
      node: groupNode,
      duration: ANIMATION_DURATION,
      opacity: targetOpacity,
      easing: Konva.Easings.EaseInOut,
    });

    const shapeTween = new Konva.Tween({
      node: shapeNode,
      duration: ANIMATION_DURATION,
      shadowBlur: targetShadowBlur,
      shadowOpacity: targetShadowOpacity,
      shadowColor: glowColor,
      easing: Konva.Easings.EaseInOut,
    });

    groupTween.play();
    shapeTween.play();
    tweensRef.current.push(groupTween, shapeTween);

    if (isHighlighted === true) {
      groupNode.moveToTop();
    } else if (isHighlighted === null && originalZIndexRef.current !== null) {
      groupNode.zIndex(originalZIndexRef.current);
    }

    return () => {
      if (tweensRef.current) {
        tweensRef.current.forEach((t) => t.destroy());
        tweensRef.current = [];
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHighlighted, groupRef, shapeRef, ...updateDeps]);
};

// ============================================================================
// Segment Setup (Positioning only)
// ============================================================================

const useSegmentSetup = (
  segment: TRenderableSegment,
  pixelsPerSecond: number,
  rowY: number,
  rowHeight: number,
  onSegmentClick?: (time: number) => void,
  collectionId?: string,
) => {
  const groupRef = useRef<Konva.Group>(null);
  const setHoveredTimelineSegment = useTimelineStore((state) => state.setHoveredTimelineSegment);
  const clearHoveredTimelineSegment = useTimelineStore((state) => state.clearHoveredTimelineSegment);
  const hoverId = collectionId ?? focusTargetId(segment.id);
  const canFocus = !!segment.groupId;
  const canHover = canFocus || segment.type === "clip";
  const focusId = canFocus ? hoverId : null;

  const x = segment.start * pixelsPerSecond;
  const height = rowHeight - PADDING_Y * 2;
  const y = rowY + PADDING_Y;

  const handleClick = useCallback(
    (event: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      event.cancelBubble = true;
      event.evt.stopPropagation();
      // Native focus changes can blur the previous label and clear this bar's
      // hover before click fires. Restore the mouse preview; taps do not hover.
      if (canHover && event.type === "click") setHoveredTimelineSegment(segment.id, focusId, segment.visibleRange);
      // Canvas segments navigate; label rows own pinned focus and expansion.
      onSegmentClick?.(segment.start);
    },
    [canHover, focusId, onSegmentClick, segment.id, segment.start, segment.visibleRange, setHoveredTimelineSegment],
  );

  const handleMouseEnter = useCallback(
    (event: Konva.KonvaEventObject<MouseEvent>) => {
      const container = event.target.getStage()?.container();
      if (container) container.style.cursor = "pointer";
      if (canHover) setHoveredTimelineSegment(segment.id, focusId, segment.visibleRange);
    },
    [canHover, focusId, segment.id, segment.visibleRange, setHoveredTimelineSegment],
  );

  const handleMouseLeave = useCallback(
    (event: Konva.KonvaEventObject<MouseEvent>) => {
      const container = event.target.getStage()?.container();
      if (container) container.style.cursor = "default";
      clearHoveredTimelineSegment(segment.id, segment.visibleRange);
    },
    [segment.id, segment.visibleRange, clearHoveredTimelineSegment],
  );

  return { groupRef, x, y, height, handleClick, handleMouseEnter, handleMouseLeave };
};

// ============================================================================
// Components
// ============================================================================

const PointMarker: React.FC<PointMarkerProps> = React.memo(
  ({
    segment,
    pixelsPerSecond,
    rowY,
    rowHeight,
    collectionId,
    onSegmentClick,
    isHighlighted,
    coverageAdditions: _coverageAdditions,
    statusRuns: _statusRuns,
    growth: _growth,
    retraction: _retraction,
    ref: _,
    ...restProps
  }) => {
    const shapeRef = useRef<Konva.Line>(null);

    const { base: colorBase, light: colorLight } = colorsFor(segment);

    const { groupRef, x, y, height, handleClick, handleMouseEnter, handleMouseLeave } = useSegmentSetup(
      segment,
      pixelsPerSecond,
      rowY,
      rowHeight,
      onSegmentClick,
      collectionId,
    );

    useSegmentVisuals(groupRef, shapeRef, isHighlighted, colorBase, [POINT_MARKER_SIZE, segment.label]);

    const centerY = height / 2;
    const halfSize = POINT_MARKER_SIZE / 2;
    const diamondPoints = [0, -halfSize, halfSize, 0, 0, halfSize, -halfSize, 0];

    return (
      <Group perfectDrawEnabled={false} ref={groupRef} x={x} y={y}>
        <Line
          {...restProps}
          closed={true}
          fillLinearGradientColorStops={[0, colorBase, 1, colorLight]}
          fillLinearGradientEndPoint={{ x: 0, y: -halfSize }}
          fillLinearGradientStartPoint={{ x: 0, y: halfSize }}
          listening={true}
          name="segment"
          perfectDrawEnabled={false}
          points={diamondPoints}
          ref={shapeRef}
          shadowColor={colorBase}
          x={0}
          y={centerY}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTap={handleClick}
        />
      </Group>
    );
  },
);

PointMarker.displayName = "PointMarker";

const DurationRect: React.FC<DurationRectProps> = React.memo(
  ({
    segment,
    pixelsPerSecond,
    rowY,
    rowHeight,
    collectionId,
    onSegmentClick,
    isHighlighted,
    coverageAdditions,
    statusRuns,
    growth,
    retraction,
    ref: _,
    ...restProps
  }) => {
    // Guard against malformed segments (start ≥ end). Konva's rounded-rect
    // path runs cornerRadius through `Math.min(cornerRadius, width / 2,
    // height / 2)`; a negative width yields a negative radius, which makes
    // ctx.arc() throw synchronously and crash the page. Producers should
    // already normalise the order — this is a belt-and-braces stop.
    const width = Math.max(0, (segment.end! - segment.start) * pixelsPerSecond);
    const shapeRef = useRef<Konva.Rect>(null);

    const { base: colorBase, light: colorLight } = colorsFor(segment);

    const { groupRef, x, y, height, handleClick, handleMouseEnter, handleMouseLeave } = useSegmentSetup(
      segment,
      pixelsPerSecond,
      rowY,
      rowHeight,
      onSegmentClick,
      collectionId,
    );

    const visibleRuns = useMemo(
      () =>
        statusRuns?.flatMap((run) => {
          const start = Math.max(segment.start, run.start);
          const end = Math.min(segment.end ?? segment.start, run.end);
          return run.state !== "unavailable" && start < end ? [{ ...run, start, end }] : [];
        }),
      [statusRuns, segment.start, segment.end],
    );
    useSegmentVisuals(groupRef, shapeRef, isHighlighted, segment.type === "clip" ? colorLight : colorBase, [
      width,
      segment.label,
    ]);
    useCoverageReveal(shapeRef, segment, pixelsPerSecond, visibleRuns, coverageAdditions, growth, retraction);

    if (width <= 0 || height <= 0) return null;

    return (
      <Group perfectDrawEnabled={false} ref={groupRef} x={x} y={y}>
        {/* A centered stroke would extend past the timestamps and cover the fill's true endpoints. */}
        <Rect
          {...restProps}
          cornerRadius={4}
          fillLinearGradientColorStops={[0, colorBase, 1, colorLight]}
          fillLinearGradientEndPoint={{ x: 0, y: 0 }}
          fillLinearGradientStartPoint={{ x: 0, y: height }}
          height={height}
          listening={true}
          name="segment"
          perfectDrawEnabled={false}
          ref={shapeRef}
          shadowColor={colorBase}
          timelineSegmentId={segment.id}
          timelineStatusRuns={visibleRuns}
          width={width}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTap={handleClick}
        />
      </Group>
    );
  },
);

DurationRect.displayName = "DurationRect";

export const Segment: React.FC<SegmentProps> = React.memo((props) => {
  const { segment } = props;
  const isPointInTime = !segment.end || segment.end === segment.start;

  if (isPointInTime) {
    return <PointMarker {...props} />;
  }
  return <DurationRect {...props} />;
});

Segment.displayName = "Segment";
